import Image from '@components/Image';
import SvgIcon from '@components/SvgIcon';
import { TipWrapper } from '@components/TipWrapper';
import IconTrash from '@components/icons/IconTrash';
import { $tourData } from '@stores/dataStores';
import { $sandTableMatrixMap } from '@stores/sandTableStores';
import { ImageType } from '@types';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
import { SortableElement } from 'react-sortable-hoc';
import styled from 'styled-components';
import { isSTImage, isSTMap, isSTMatrix } from '../utils/sandTableTypeCheck';

interface Props {
  id: string;
  isSelected?: boolean;
  isDefault?: boolean;
  className?: string;
  deleteDisabled?: boolean;
  onClick: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onSetDefaultClick: (id: string) => void;
}

export const SandTableListItem = SortableElement(
  ({
    id,
    isSelected,
    isDefault,
    className,
    deleteDisabled,
    onClick,
    onDeleteClick,
    onSetDefaultClick,
  }: Props) => {
    const matrixMap = useStore($sandTableMatrixMap);
    const data = useStoreMap({
      store: $tourData,
      keys: [id],
      fn: (state) => state.sandBox?.find((item) => item.id === id),
    });

    if (!data) return null;
    const getText = () => (isSTMap(data) ? '地图' : '图片');

    const renderCover = () => {
      if (isSTMap(data)) {
        return (
          <Image
            src="/imgs/icon-stl-map.c1f7b230e975078d83dc47d97e05f901.png"
            type={ImageType.sysMedia}
            className="h-full w-full"
            size={16}
          />
        );
      }
      if (isSTImage(data)) {
        return (
          <Image
            src={data.mediaUrl}
            type={ImageType.media}
            className="h-full w-full"
            size={16}
          />
        );
      }
      if (isSTMatrix(data)) {
        return (
          <Image
            src={matrixMap.get(data.data)?.thumb ?? ''}
            type={ImageType.thumb}
            className="h-full w-full"
            size={16}
          />
        );
      }
    };

    return (
      <div
        className={classNames(
          'mb-10 flex h-35 w-full cursor-pointer select-none items-center rounded px-10',
          {
            'ring-2 ring-inset ring-orange': isSelected,
          },
          className
        )}
        onClick={() => onClick(id)}
      >
        <div className="relative h-16 w-16 shrink-0">
          {renderCover()}
          {!!isDefault && (
            <Image
              src="/imgs/icon-st-default.041b24650e28eca44adafdac7cba5ea0.png"
              type={ImageType.sysMedia}
              className="absolute inset-0"
              size={16}
            />
          )}
        </div>
        <div className="mx-10 truncate text-base text-white">{data.name}</div>
        <div className="ml-auto flex-shrink-0 text-sm text-black-gray">
          {getText()}
        </div>
        <TipWrapper
          text="设为默认"
          placement="bottom"
          className="ml-10"
          size="sm"
        >
          <IconSetDefault
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onSetDefaultClick(id);
            }}
          />
        </TipWrapper>
        <TipWrapper
          text={'删除'}
          placement="bottom"
          className="ml-10"
          size="sm"
        >
          <IconTrash
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              if (deleteDisabled) return;
              onDeleteClick(id);
            }}
          />
        </TipWrapper>
      </div>
    );
  }
);

const IconSetDefault = styled(SvgIcon).attrs({
  children: ({ color }: { color: string }) => (
    <svg fill="none" width="16" height="16">
      <clipPath id="prefix_iL6c_uF82ji8iJKHCF-Ga">
        <rect width="16" height="16" rx="0" />
      </clipPath>
      <g clipPath="url(#prefix_iL6c_uF82ji8iJKHCF-Ga)">
        <rect width="16" height="16" rx="0" fill="none" />
        <path
          d="M8 2 4 6h3v5h2V6h3L8 2ZM2 14v-2h12v2H2Z"
          fillRule="evenodd"
          fill={color}
        />
      </g>
    </svg>
  ),
})``;
